/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: index.ts
 * Class: index.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import _Vue from 'vue';
import { inject, provide } from '@vue/composition-api';
import { TrackingEvent, UniversalAnalytics } from './types/tracking';
import { ConfigurationPluginManager } from '../configuration';
import { PaymentResponse } from '../../store/payment/types';
import { Basket, CheckoutData } from '../../store/order/types';
import { BasePaymentStorage } from '../../service/storage/types/BaseStorage';

export class TrackingPlugin {
  constructor() {
    if (!window.dataLayer) {
      throw new Error('GTM datalayer is undefined, please check GTM integration!');
    }
  }

  isOptimizeTest(testId: string): boolean {
    return window.google_optimize
      && window.google_optimize.get
        && window.google_optimize.get(testId) === '1';
  }

  trackEnhancedEcommerce(payment: BasePaymentStorage, checkoutData?: CheckoutData): void {
    const universalAnalytics: UniversalAnalytics = {
      transactionId: payment.id,
      transactionTotal: payment.amount,
      currencyCode: payment.currency,
      transactionProducts: [],
    };

    // --- recharge account actions does not have a basket, but we still can track them according
    // --- to https://support.google.com/tagmanager/answer/6107169?hl=de
    if (checkoutData && checkoutData.basket) {
      const { basket } = checkoutData;

      // --- referer only in the basket
      if (checkoutData.basket.reference_id) {
        universalAnalytics.transactionAffiliation = checkoutData.basket.reference_id;
      }

      universalAnalytics.transactionProducts.push({
        sku: `${checkoutData.basket.productId}-${checkoutData.basket.durationDays}`,
        name: `${checkoutData.info.productName ?? '?'} (${checkoutData.basket.durationDays} days)`,
        category: checkoutData.info.productName ?? '?',
        price: Math.round(checkoutData.price.amount / basket.units),
        quantity: checkoutData.basket.units,
      });
    }

    this.track({ event: 'UNI_ANA', data: universalAnalytics });
  }

  track<T>(data: TrackingEvent<T>) {
    if (window.dataLayer) {
      window.dataLayer.push(data);
    }
  }
}

export function VueTrackingPlugin(Vue: typeof _Vue, options?): void {
  Vue.prototype.$tracking = new TrackingPlugin();
}

// ### Composition API Usage ###
const trackingSymbol = Symbol('Vue Tracking Plugin');

export function provideTracking() {
  provide(trackingSymbol, new TrackingPlugin());
}

export const useTracking = (): TrackingPlugin => {
  const config = inject<TrackingPlugin>(trackingSymbol);
  if (!config) throw new Error('Tracking Plugin could not be provided!');

  return config;
};

export const tracking = new TrackingPlugin();
